import { forwardRef, useEffect, useRef } from "react";

import { Box, type BoxProps } from "@hightouch/htui";
import { useQueryClient } from "react-query";

import { isPendingId } from "src/pages/projects/board-cache";
import { PROJECTS_PATHS } from "src/pages/projects/constants";
import {
  prefetchProjectPage,
  prefetchTaskPage,
} from "src/pages/projects/prefetch";
import { RouterLink, useParams } from "src/router";

// Long enough that sweeping the pointer across a list doesn't fetch every row.
const HOVER_PREFETCH_DELAY_MS = 80;

type LinkProps = Omit<BoxProps, "as"> & {
  "aria-label"?: string;
};

/**
 * Hover and focus handlers that warm a page's data a moment before it is
 * opened, so it renders from the cache.
 */
function usePrefetchIntent(prefetch: () => void) {
  const timer = useRef<number>();
  useEffect(() => () => window.clearTimeout(timer.current), []);
  return {
    onMouseEnter: () => {
      window.clearTimeout(timer.current);
      timer.current = window.setTimeout(prefetch, HOVER_PREFETCH_DELAY_MS);
    },
    onMouseLeave: () => window.clearTimeout(timer.current),
    // A press comes before the click by about 100ms, and a touch has no hover to start sooner.
    onPointerDown: () => {
      window.clearTimeout(timer.current);
      prefetch();
    },
    onFocus: prefetch,
  };
}

/** Links to a task's page, loading it on hover. */
export const TaskLink = forwardRef<
  HTMLAnchorElement,
  LinkProps & { taskId: string }
>(({ taskId, ...props }, ref) => {
  const queryClient = useQueryClient();
  // Inside a project, a task link stays under it, so the area's navigation keeps the project open.
  const { projectId } = useParams<{ projectId?: string }>();
  const intent = usePrefetchIntent(() => prefetchTaskPage(queryClient, taskId));
  // A task still being created has no page yet.
  if (isPendingId(taskId)) return <Box {...props} />;
  return (
    <Box
      ref={ref}
      as={RouterLink}
      to={
        projectId
          ? PROJECTS_PATHS.projectTask(projectId, taskId)
          : PROJECTS_PATHS.task(taskId)
      }
      {...intent}
      {...props}
    />
  );
});
TaskLink.displayName = "TaskLink";

/** Links to a project's page, loading it on hover. */
export const ProjectLink = forwardRef<
  HTMLAnchorElement,
  LinkProps & { projectId: string }
>(({ projectId, ...props }, ref) => {
  const queryClient = useQueryClient();
  const intent = usePrefetchIntent(() =>
    prefetchProjectPage(queryClient, projectId),
  );
  return (
    <Box
      ref={ref}
      as={RouterLink}
      to={PROJECTS_PATHS.project(projectId)}
      {...intent}
      {...props}
    />
  );
});
ProjectLink.displayName = "ProjectLink";
